// src/pages/admin/ResumoAusencias.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";
import { useTheme } from "@mui/material/styles";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import PageHeader from "pages/components/PageHeader";
import { abrirRelatorio } from "pages/utils/abrirRelatorio";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";

import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// ------------------- Filtros por coluna -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

function NumberFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="nº..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
        textAlign: "right",
      }}
    />
  );
}

// ------------------- Persistência de filtros -------------------
const FILTERS_KEY = "tbl:resumo-ausencias:filters";
const KEEP_KEY = "tbl:resumo-ausencias:keep";

const fmtNum = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString("pt-PT", { maximumFractionDigits: 1 });
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yy = dt.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const tipoDia = (a) => {
  const fr = Number(a?.fracao || 0);
  if (fr === 1) {
    const p = Number(a?.periodo || 0);
    if (p === 1) return "Meio dia (manhã)";
    if (p === 2) return "Meio dia (tarde)";
    return "Meio dia";
  }
  return "Dia inteiro";
};

const estadoTxt = (r) => {
  const v = Number(r?.estado);
  const cancel = Number(r?.cancelado ?? 0) === 1;

  if (v === 1 && cancel) return "Cancelado";
  if (v === 1) return "Validado";
  if (v === 2) return "Recusado";
  return "Pendente";
};

export default function ResumoAusencias() {
  const { showAlert } = useAlert();
  const { userData } = useAuth();
  const theme = useTheme();

  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);

  const now = new Date();

  const primeiroDiaMesAnterior = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const ultimoDiaMesAnterior = new Date(now.getFullYear(), now.getMonth(), 0);

  const toInputDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
      2,
      "0"
    )}`;

  const [openResumoMes, setOpenResumoMes] = useState(false);

  const [dataInicialResumoMes, setDataInicialResumoMes] = useState(
    toInputDate(primeiroDiaMesAnterior)
  );

  const [dataFinalResumoMes, setDataFinalResumoMes] = useState(toInputDate(ultimoDiaMesAnterior));

  const [loadingResumoMes, setLoadingResumoMes] = useState(false);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;

  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // ---------- Dialog detalhe ----------
  const [openDet, setOpenDet] = useState(false);
  const [detUser, setDetUser] = useState(null);
  const [detRows, setDetRows] = useState([]);
  const [loadingDet, setLoadingDet] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(null); // string | null

  const carregar = async (anoSel) => {
    setLoading(true);
    const body = { ano: anoSel, idutilizador: userData?.id, funcoes: userData?.funcoes ?? [] };

    const [payload, err, meta] = await handleApi(api.post("Ausencias/SelResumoAusenciasAno", body));
    setLoading(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    // meta.message pode vir com “Sem funções geridas...”
    if (meta?.message) showAlert(meta.message, meta?.severity ?? "info");

    setData(Array.isArray(payload) ? payload : []);
  };

  async function tryParseJsonBlob(blob) {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  }

  async function gerarResumoMes(tipo) {
    setLoadingResumoMes(true);
    try {
      const res = await api.post(
        "Relatorios/ObterRptAusenciasMensal",
        {
          tipo,
          par_datainicial: dataInicialResumoMes,
          par_datafinal: dataFinalResumoMes,
        },
        { responseType: "blob" }
      );

      const contentType = res.headers["content-type"] || "";
      const blob = new Blob([res.data], { type: contentType });

      // PDF via AJAX → JSON com URL efémero
      if (contentType.includes("application/json")) {
        const j = await tryParseJsonBlob(blob);
        const url = j?.data?.url;
        if (url) {
          abrirRelatorio(url);
          setLoadingResumoMes(false);
          return;
        }
        showAlert(j?.message || "Erro ao gerar relatório.", "error");
        setLoadingResumoMes(false);
        return;
      }

      // XLSX → download direto
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Ausencias_${dataInicialResumoMes}_${dataFinalResumoMes}.${
        tipo === "xlsx" ? "xlsx" : tipo
      }`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setLoadingResumoMes(false);
    } catch (e) {
      setLoadingResumoMes(false);
      showAlert(e?.message || "Erro de comunicação ao gerar relatório.", "error");
    }
  }

  const abrirDetalhe = async (u) => {
    setDetUser(u);
    setDetRows([]);
    setOpenDet(true);

    setLoadingDet(true);
    const body = {
      ano,
      idutilizador: u?.idutilizador,
      idutilizador_solicitante: userData?.id,
      funcoes: userData?.funcoes ?? [],
    };

    const [payload, err] = await handleApi(api.post("Ausencias/SelResumoAusenciasDetalhe", body));
    setLoadingDet(false);

    if (err) {
      showAlert(err, "error");
      return;
    }

    setDetRows(Array.isArray(payload) ? payload : []);
  };

  const fecharDetalhe = () => {
    setOpenDet(false);
    setDetUser(null);
    setDetRows([]);
  };

  useEffect(() => {
    carregar(ano);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ano]);

  // guardar keep
  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
      if (!keepFilters) {
        localStorage.removeItem(FILTERS_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [keepFilters]);

  // ------------------- Colunas -------------------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 56,
        minWidth: 56,
        maxWidth: 72,
        Cell: ({ row }) => {
          const u = row.original;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
              <IconButton size="small" color="info" title="Detalhe" onClick={() => abrirDetalhe(u)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </div>
          );
        },
      },
      { Header: "Funcionário", accessor: "utilizador", Filter: TextFilter },
      {
        Header: () => <div style={{ textAlign: "center" }}>Dias gozados</div>,
        accessor: "diasgozados",
        disableFilters: true,
        Cell: ({ value }) => (
          <span style={{ display: "block", textAlign: "center" }}>{fmtNum(value)}</span>
        ),
      },
      {
        Header: () => <div style={{ textAlign: "center" }}>Dias por gozar</div>,
        accessor: "diasporgozar",
        disableFilters: true,
        Cell: ({ value }) => (
          <span style={{ display: "block", textAlign: "center" }}>{fmtNum(value)}</span>
        ),
      },
      {
        Header: () => <div style={{ textAlign: "center" }}>Total</div>,
        id: "total",
        accessor: (row) => Number(row?.diasgozados || 0) + Number(row?.diasporgozar || 0),
        disableFilters: true,
        Cell: ({ value }) => {
          const v = Number(value || 0);

          // warning até 22 | success a 22 | error > 22
          const color =
            v > 22
              ? theme.palette.error.main
              : v === 22
              ? theme.palette.success.main
              : theme.palette.warning.main;

          return (
            <strong style={{ display: "block", textAlign: "center", color }}>{fmtNum(v)}</strong>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ano, theme]
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { filters: initialFilters },
      autoResetFilters: !keepFilters,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state } = tableInstance;

  // persistir filtros
  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters ?? []));
    } catch {
      /* ignore */
    }
  }, [state.filters, keepFilters]);

  // --------- Totais no Dialog (a partir de detRows) ----------
  const resumoDet = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const frac = (r) => (Number(r?.fracao) === 1 ? 0.5 : 1);
    const isPast = (r) => {
      const d = new Date(r?.dia);
      d.setHours(0, 0, 0, 0);
      return d < hoje;
    };

    let valGoz = 0,
      valPor = 0,
      pend = 0,
      rec = 0,
      canc = 0,
      totalFerias = 0;

    for (const r of detRows) {
      const est = Number(r?.estado || 0);

      const cancel = Number(r?.cancelado ?? 0) === 1;
      const ferias = Number(r?.ferias ?? 1) === 1;

      // Cancelados: contam apenas para "Cancelados"
      if (cancel) {
        canc += frac(r);
        continue;
      }

      // Só contam para férias se ferias=1
      if (ferias) totalFerias += frac(r);

      if (est === 1) {
        if (isPast(r)) valGoz += ferias ? frac(r) : 0;
        else valPor += ferias ? frac(r) : 0;
      } else if (est === 2) {
        rec += frac(r);
      } else {
        pend += frac(r);
      }
    }

    return {
      pend,
      rec,
      canc,
      valGoz,
      valPor,
      totalFerias,
    };
  }, [detRows]);

  const SummaryItem = ({ id, label, value, tooltip }) => (
    <ClickAwayListener onClickAway={() => setTooltipOpen(null)}>
      <Tooltip
        arrow
        placement="top"
        open={tooltipOpen === id}
        onClose={() => setTooltipOpen(null)}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "rgba(0,0,0,0.78)",
              color: "#fff",
              fontSize: 12,
              borderRadius: "8px",
              px: 1.2,
              py: 0.8,
              maxWidth: 320,
            },
          },
          arrow: { sx: { color: "rgba(0,0,0,0.78)" } },
        }}
        title={tooltip || ""}
      >
        <MDTypography
          style={{
            fontSize: 12,
            cursor: "pointer",
            userSelect: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 8px",
            borderRadius: 8,
            background: "rgba(0,0,0,0.04)",
          }}
          onClick={() => setTooltipOpen((prev) => (prev === id ? null : id))}
          title="Clique para ajuda"
        >
          <strong>{label}</strong> {value}
        </MDTypography>
      </Tooltip>
    </ClickAwayListener>
  );

  // ------------------- UI -------------------
  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* PROGRESS GLOBAL — Playbook */}
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox>
        <Card>
          <MDBox p={2}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={2}
            >
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDBox>
                  <MDTypography
                    variant="caption"
                    color="text"
                    style={{ fontSize: 12, marginRight: 5 }}
                  >
                    Ano
                  </MDTypography>
                  <select
                    value={ano}
                    onChange={(e) => setAno(Number(e.target.value))}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      fontSize: 13,
                      border: "1px solid rgba(0,0,0,0.2)",
                      outline: "none",
                      minWidth: 120,
                    }}
                  >
                    {Array.from({ length: 5 }).map((_, i) => {
                      const y = anoAtual - i;
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    })}
                  </select>
                </MDBox>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={keepFilters}
                      onChange={(e) => setKeepFilters(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Manter filtros"
                />
              </MDBox>

              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => {
                  const hoje = new Date();

                  const primeiroDiaMesAnterior = new Date(
                    hoje.getFullYear(),
                    hoje.getMonth() - 1,
                    1
                  );

                  const ultimoDiaMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth(), 0);

                  setDataInicialResumoMes(toInputDate(primeiroDiaMesAnterior));
                  setDataFinalResumoMes(toInputDate(ultimoDiaMesAnterior));

                  setOpenResumoMes(true);
                }}
              >
                Resumo
              </MDButton>
            </MDBox>

            <MDBox mt={2} sx={{ height: "68vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                      {hg.headers.map((col) => {
                        const isNum = ["diasgozados", "diasporgozar", "total"].includes(col.id);

                        return (
                          <th
                            {...col.getHeaderProps(col.getSortByToggleProps())}
                            key={col.id}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              background: "#fff",
                              fontSize: 12,
                              color: "#7b809a",
                              padding: "6px 8px",
                              borderBottom: "1px solid rgba(0,0,0,0.15)",
                              textAlign: col.id === "acoes" || isNum ? "center" : "left",
                              verticalAlign: "bottom",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                              width: col.id === "acoes" ? 56 : undefined,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  col.id === "acoes" || isNum ? "center" : "flex-start",
                              }}
                            >
                              {col.render("Header")}
                              {col.isSorted ? (
                                col.isSortedDesc ? (
                                  <span style={{ marginLeft: 6 }}>▼</span>
                                ) : (
                                  <span style={{ marginLeft: 6 }}>▲</span>
                                )
                              ) : null}
                            </div>

                            {!col.disableFilters && (
                              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                                {col.canFilter ? col.render("Filter") : null}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr
                        {...row.getRowProps()}
                        key={row.id}
                        style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}
                      >
                        {row.cells.map((cell) => {
                          const isNum = ["diasgozados", "diasporgozar", "total"].includes(
                            cell.column.id
                          );

                          return (
                            <td
                              {...cell.getCellProps()}
                              key={cell.column.id}
                              style={{
                                fontSize: 12,
                                padding: "8px 10px",
                                width: cell.column.id === "acoes" ? 56 : undefined,
                                whiteSpace: cell.column.id === "acoes" ? "nowrap" : "normal",
                                textAlign: cell.column.id === "acoes" || isNum ? "center" : "left",
                              }}
                            >
                              {cell.render("Cell")}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}

                  {rows.length === 0 && !loading && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{ padding: 12, fontSize: 12, color: "#667" }}
                      >
                        Sem resultados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      <Dialog open={openResumoMes} onClose={() => setOpenResumoMes(false)} fullWidth maxWidth="sm">
        <DialogTitle>Resumo Mês</DialogTitle>

        <DialogContent dividers>
          <MDBox display="flex" gap={2} flexWrap="wrap">
            <MDBox>
              <MDTypography
                variant="caption"
                color="text"
                style={{ fontSize: 12, marginBottom: 5 }}
              >
                Data Inicial
              </MDTypography>

              <input
                type="date"
                value={dataInicialResumoMes}
                onChange={(e) => setDataInicialResumoMes(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "1px solid rgba(0,0,0,0.2)",
                  outline: "none",
                  minWidth: 170,
                }}
              />
            </MDBox>

            <MDBox>
              <MDTypography
                variant="caption"
                color="text"
                style={{ fontSize: 12, marginBottom: 5 }}
              >
                Data Final
              </MDTypography>

              <input
                type="date"
                value={dataFinalResumoMes}
                onChange={(e) => setDataFinalResumoMes(e.target.value)}
                style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 13,
                  border: "1px solid rgba(0,0,0,0.2)",
                  outline: "none",
                  minWidth: 170,
                }}
              />
            </MDBox>
          </MDBox>

          {loadingResumoMes && (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}
        </DialogContent>

        <DialogActions>
          <MDBox px={1} pb={1} display="flex" alignItems="center" gap={1} width="100%">
            <Tooltip title="Abrir PDF">
              <span>
                <IconButton
                  color="info"
                  disabled={loadingResumoMes}
                  onClick={() => gerarResumoMes("pdf")}
                >
                  <PictureAsPdfIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Exportar Excel">
              <span>
                <IconButton
                  color="info"
                  disabled={loadingResumoMes}
                  onClick={() => gerarResumoMes("xlsx")}
                >
                  <GridOnIcon />
                </IconButton>
              </span>
            </Tooltip>

            <MDBox flex={1} />

            <MDButton
              variant="text"
              color="dark"
              onClick={() => setOpenResumoMes(false)}
              disabled={loadingResumoMes}
            >
              Fechar
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>

      {/* Dialog detalhe */}
      <Dialog open={openDet} onClose={fecharDetalhe} fullWidth maxWidth="md">
        <DialogTitle>
          Detalhe de Ausências — {detUser?.utilizador ?? ""}
          <span style={{ display: "block", fontSize: 12, fontWeight: 400, opacity: 0.8 }}>
            Ano {ano}
          </span>
        </DialogTitle>

        <DialogContent dividers>
          {loadingDet ? (
            <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : (
            <>
              <MDBox mb={2} display="flex" gap={1.5} flexWrap="wrap">
                <SummaryItem
                  id="pend"
                  label="Pendentes:"
                  value={fmtNum(resumoDet.pend)}
                  tooltip="Pedidos ainda não analisados por um gestor."
                />

                <SummaryItem
                  id="rec"
                  label="Recusados:"
                  value={fmtNum(resumoDet.rec)}
                  tooltip="Pedidos recusados pelo gestor (não contam para férias nem para ocupação após a decisão)."
                />

                <SummaryItem
                  id="canc"
                  label="Cancelados:"
                  value={fmtNum(resumoDet.canc)}
                  tooltip="Pedidos que chegaram a ser validados, mas foram cancelados com motivo. Estes dias ficam novamente disponíveis."
                />

                <SummaryItem
                  id="valGoz"
                  label="Validados (gozados):"
                  value={fmtNum(resumoDet.valGoz)}
                  tooltip="Pedidos validados cuja data já passou. Só contam para férias se 'Férias = Sim'."
                />

                <SummaryItem
                  id="valPor"
                  label="Validados (por gozar):"
                  value={fmtNum(resumoDet.valPor)}
                  tooltip="Pedidos validados cuja data ainda não chegou. Só contam para férias se 'Férias = Sim'."
                />

                <SummaryItem
                  id="totalFerias"
                  label="Total Férias:"
                  value={fmtNum(resumoDet.totalFerias)}
                  tooltip="Soma dos dias que contam para férias: apenas pedidos validados, não cancelados, com 'Férias = Sim'."
                />
              </MDBox>

              <MDBox style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[
                        "Dia",
                        "Tipo",
                        "Férias",
                        "Estado",
                        "Função",
                        "Obs. pedido",
                        "Obs. validação/recusa",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            fontSize: 12,
                            textAlign: "left",
                            padding: "8px 6px",
                            borderBottom: "1px solid rgba(0,0,0,0.15)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detRows.map((r) => (
                      <tr
                        key={r.id}
                        style={{
                          borderBottom: "1px solid rgba(0,0,0,0.06)",
                          textDecoration: Number(r?.cancelado ?? 0) === 1 ? "line-through" : "none",
                          opacity: Number(r?.cancelado ?? 0) === 1 ? 0.7 : 1,
                        }}
                      >
                        <td style={{ fontSize: 12, padding: "8px 6px", whiteSpace: "nowrap" }}>
                          {fmtDate(r.dia)}
                        </td>
                        <td style={{ fontSize: 12, padding: "8px 6px", whiteSpace: "nowrap" }}>
                          {tipoDia(r)}
                        </td>

                        {/* Férias (Sim/Não) */}
                        <td
                          style={{
                            fontSize: 12,
                            padding: "8px 6px",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                          }}
                        >
                          {Number(r?.ferias ?? 1) === 1 ? "Sim" : "Não"}
                        </td>

                        <td style={{ fontSize: 12, padding: "8px 6px", whiteSpace: "nowrap" }}>
                          {estadoTxt(r)}
                        </td>

                        <td style={{ fontSize: 12, padding: "8px 6px", whiteSpace: "nowrap" }}>
                          {r.funcao ?? ""}
                        </td>
                        <td style={{ fontSize: 12, padding: "8px 6px" }}>{r.motivo ?? ""}</td>
                        <td style={{ fontSize: 12, padding: "8px 6px" }}>
                          {Number(r?.cancelado ?? 0) === 1
                            ? r.motivo_cancelamento ?? ""
                            : r.estado === 2
                            ? r.motivo_rejeicao ?? ""
                            : r.override_motivo ?? ""}
                        </td>
                      </tr>
                    ))}

                    {detRows.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ padding: 12, fontSize: 12, color: "#667" }}>
                          Sem registos para o ano selecionado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <MDBox px={1} pb={1}>
            <IconButton onClick={fecharDetalhe} title="Fechar">
              ✕
            </IconButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
